import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { queryKeys } from '~/api/queryKeys';
import { useActorResolver } from '~/hooks/use-actor-resolver';
import { jitteredStaleTime } from '~/utils/jitter';

import { getRunCreators } from '../services/runs.service';
import type { RunCreator } from '../types/runs.api.types';
import type { RunsFilterParams } from '../types/runs.types';
import { isValidationOfField, readWithFallback } from '../utils/catalog-capabilities';

import { useIsCurrentUser } from './use-runs-actor';

const CREATORS_STALE_MS = 60_000;

/**
 * The "Created by" filter's options (GET catalog-runs/creators) under the list filters, or `null`
 * on a backend without the route — an older deploy routes `catalog-runs/creators` to
 * `catalog-runs/{run_id}` and 422s on `path.run_id`, which counts as missing for the session.
 * `enabled` lets the filter bar read it only once someone opens Filters or a pick needs a label.
 */
export function useRunCreators(params: RunsFilterParams, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogService.runs.creators({ ...params }),
    queryFn: async (): Promise<RunCreator[] | null> => {
      const result = await readWithFallback({
        capability: 'runCreators',
        read: () => getRunCreators(params),
        fallback: () => Promise.resolve(null),
        missingWhen: (error) => isValidationOfField(error, 'path.run_id'),
      });
      return result.data;
    },
    enabled,
    // Other filters change the key; keep the last options listed (and the field usable) meanwhile.
    placeholderData: keepPreviousData,
    staleTime: jitteredStaleTime(CREATORS_STALE_MS),
  });
}

/**
 * Names a run creator the way the run grid names a starter: "You" for the signed-in user, the
 * server's handle, else the member list's name (same stopgap as `useRunsActor`), else a short
 * id. Non-person actors are marked so a service account never reads as a teammate.
 */
export function useRunCreatorLabel() {
  const { t } = useTranslation('catalog-enrichment-runs');
  const resolveActor = useActorResolver();
  const isMe = useIsCurrentUser();

  return (creator: Pick<RunCreator, 'actorId' | 'handle'> & { kind?: RunCreator['kind'] }) => {
    if (isMe(creator.actorId) || isMe(creator.handle)) return t('filters.creator.you');
    // A pick outside the current options has no kind — still worth a member-list lookup.
    const resolved = !creator.handle
      ? resolveActor({
          id: creator.actorId,
          displayName: '',
          kind: creator.kind ?? 'UNKNOWN',
          email: null,
          avatarUrl: null,
          resolved: false,
        })
      : null;
    const name =
      creator.handle ??
      (resolved?.resolved ? resolved.displayName : null) ??
      t('filters.creator.shortId', { id: creator.actorId.slice(0, 8) });
    return creator.kind === 'SERVICE' || creator.kind === 'SYSTEM'
      ? t('filters.creator.nonPerson', { name, kind: t(`filters.creator.kind.${creator.kind}`) })
      : name;
  };
}
